/* eslint-disable react-refresh/only-export-components */
// context/DarkModeContext.tsx (updated)
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  type ReactNode,
} from "react";

interface DarkModeContextType {
  isDark: boolean;
  toggleDarkMode: (enabled?: boolean) => void;
}

export const DarkModeContext = createContext<DarkModeContextType | undefined>(
  undefined,
);

export const useDarkMode = () => {
  const context = useContext(DarkModeContext);
  if (context === undefined) {
    throw new Error("useDarkMode must be used within a DarkModeProvider");
  }
  return context;
};

interface DarkModeProviderProps {
  children: ReactNode;
}

export const DarkModeProvider: React.FC<DarkModeProviderProps> = ({
  children,
}) => {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    // Check for saved theme preference or system preference
    const savedPreferences = localStorage.getItem("userPreferences");
    if (savedPreferences) {
      const preferences = JSON.parse(savedPreferences);
      const darkModeEnabled = preferences.darkMode || false;
      setIsDark(darkModeEnabled);

      if (darkModeEnabled) {
        document.documentElement.classList.add("dark");
      } else {
        document.documentElement.classList.remove("dark");
      }
    } else {
      // Check system preference as fallback
      const systemPrefersDark = window.matchMedia(
        "(prefers-color-scheme: dark)",
      ).matches;
      if (systemPrefersDark) {
        setIsDark(true);
        document.documentElement.classList.add("dark");
      }
    }
  }, []);

  const toggleDarkMode = (enabled?: boolean) => {
    const newDarkMode = enabled !== undefined ? enabled : !isDark;
    setIsDark(newDarkMode);

    if (newDarkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }

    // Save to localStorage
    const savedPreferences = localStorage.getItem("userPreferences");
    const preferences = savedPreferences ? JSON.parse(savedPreferences) : {};

    const updatedPreferences = {
      ...preferences,
      darkMode: newDarkMode,
    };

    localStorage.setItem("userPreferences", JSON.stringify(updatedPreferences));
  };

  return (
    <DarkModeContext.Provider value={{ isDark, toggleDarkMode }}>
      {children}
    </DarkModeContext.Provider>
  );
};
